import { Button } from '@cloudflare/kumo';
import { useState } from 'react';
import { DataTable as Table } from './ui/DataTable';
import { DangerButton } from './ui/DangerButton';
import { DangerConfirmModal } from './DangerConfirmModal';
import { Category } from '../types';
import { SkeletonTable } from './ui/Skeleton';

interface CategoryListProps {
  categories: Category[];
  loading: boolean;
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CategoryList({ categories, loading, onEdit, onDelete }: CategoryListProps) {
  // Exclusão só depois de digitar DELETE ou o nome da categoria
  const [pendingDelete, setPendingDelete] = useState<{ id: string; nome: string } | null>(null);

  if (loading) {
    return (
      <SkeletonTable rows={5} cols={3} label="Carregando categorias" />
    );
  }

  if (categories.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="!text-[var(--text-faint)]">Nenhuma categoria encontrada.</p>
      </div>
    );
  }

  const columnDefinitions = [
    {
      id: 'cor',
      header: 'Cor',
      cell: (category: Category) => (
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded border border-[var(--border)] flex-shrink-0"
            style={{ backgroundColor: category.cor }}
            aria-label={`Cor: ${category.cor}`}
          />
          <code className="font-mono text-sm text-xs">{category.cor}</code>
        </div>
      ),
    },
    {
      id: 'nome',
      header: 'Nome',
      cell: (category: Category) => <p className="font-medium !text-[var(--text)]">{category.nome}</p>,
    },
    {
      id: 'descricao',
      header: 'Descrição',
      cell: (category: Category) => <p className="!text-[var(--text-muted)]">{category.descricao || '—'}</p>,
    },
    {
      id: 'atualizadoEm',
      header: 'Atualizado em',
      cell: (category: Category) => <p className="!text-[var(--text-muted)] text-xs">{formatDate(category.atualizadoEm)}</p>,
    },
    {
      id: 'acoes',
      header: 'Ações',
      cell: (category: Category) => (
        <div className="flex gap-2">
          <Button variant="secondary" aria-label={`Editar ${category.nome}`} onClick={() => onEdit(category)}>
            Editar
          </Button>
          <DangerButton
            variant="dangerOutline"
            onClick={() => setPendingDelete({ id: category.id, nome: category.nome })}
            aria-label={`Excluir ${category.nome}`}
          >
            Excluir
          </DangerButton>
        </div>
      ),
    },
  ];

  return (
    <div className="overflow-x-auto">
      <Table
        items={categories}
        columnDefinitions={columnDefinitions}
        trackBy={(category: Category) => category.id}
        wrapLines
        ariaLabels={{
          tableLabel: 'Categorias',
          selectionGroupLabel: 'Categorias',
        }}
      />
      <DangerConfirmModal
        open={pendingDelete !== null}
        title={`Excluir a categoria "${pendingDelete?.nome ?? ''}"?`}
        confirmText="Excluir"
        objectName={pendingDelete?.nome}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => { const t = pendingDelete; setPendingDelete(null); if (t) onDelete(t.id); }}
      >
        A categoria é excluída. Os contatos dela continuam cadastrados, sem categoria.
      </DangerConfirmModal>
    </div>
  );
}
